import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { userAtom } from "../../recoil/userAtom";
import { useRecoilValue } from "recoil";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import useMixpanelWeb from "../../hooks/useMixpanelWeb";
import Demographic_PS_1 from "./part/demographic/Demographic_PS_1";
import Demographic_PS_4 from "./part/demographic/Demographic_PS_4";
import Demographic_PS_3 from "./part/demographic/Demographic_PS_3";
import Demographic_PS_2 from "./part/demographic/Demographic_PS_2";
import { useOnboardingProgress } from "../../hooks/useOnboardingProgress";
import FullPageWithCenterContentV2 from "../container/FullPageWithCenterContentV2";
import FullPageWithHeaderAndProgress from "../container/FullPageWithHeaderAndProgress";
import { theme } from "../../styles/theme";
import Goal_PS_1 from "./part/goal/Goal_PS_1";
import Goal_PS_2_MultiSelect_1 from "./part/goal/Goal_PS_2_MultiSelect_1";
import Goal_PS_3_MultiSelect_2 from "./part/goal/Goal_PS_3_MultiSelect_2";
import Goal_PS_4 from "./part/goal/Goal_PS_4";
import Goal_PS_5 from "./part/goal/Goal_PS_5";
import Goal_PS_6 from "./part/goal/Goal_PS_6";
import Goal_PS_7 from "./part/goal/Goal_PS_7";
import Goal_PS_8 from "./part/goal/Goal_PS_8";
import Goal_PS_9 from "./part/goal/Goal_PS_9";
import Goal_PS_10 from "./part/goal/Goal_PS_10";
import Goal_PS_11 from "./part/goal/Goal_PS_11";
import Goal_PS_12 from "./part/goal/Goal_PS_12";
import { ProgramSetup_1_Load } from "./part/load/ProgramSetup_1_Load";
import useClose from "../../hooks/useClose";
import { useMutation } from "@tanstack/react-query";
import { programSetupApi } from "../../api/programSetupApi";

interface ProgramSetupStep {
  type: string;
  seqByType: number;
  title?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  disableBack?: boolean
  hideBack?: boolean;
  hideClose?: boolean;
  progressDelta?: number;
  centerFullScreen?: boolean;
  backgroundColor?: string;
  paddingXContent?: number;
  content: React.ReactNode;
}

const commonSteps: ProgramSetupStep[] = [
  {
    type: "load",
    seqByType: 1,
    hideHeader: true,
    hideProgress: true,
    content: <ProgramSetup_1_Load />,
  },
  {
    type: "demographic",
    seqByType: 1,
    title: "기본 정보",
    progressDelta: 1 / 4,
    disableBack: true,
    content: <Demographic_PS_1 />,
  },
  {
    type: "demographic",
    seqByType: 2,
    title: "기본 정보",
    progressDelta: 2 / 4,
    content: <Demographic_PS_2 />,
  },
  {
    type: "demographic",
    seqByType: 3,
    title: "기본 정보",
    progressDelta: 3 / 4,
    content: <Demographic_PS_3 />,
  },
  {
    type: "demographic",
    seqByType: 4,
    title: "기본 정보",
    progressDelta: 4 / 4,
    content: <Demographic_PS_4 />,
  },
  {
    type: "goal",
    seqByType: 1,
    title: "고민과 목표",
    progressDelta: 1 / 7,
    content: <Goal_PS_1 />,
  },
  {
    type: "goal",
    seqByType: 2,
    title: "고민과 목표",
    progressDelta: 2 / 7,
    content: <Goal_PS_2_MultiSelect_1 />,
  },
  {
    type: "goal",
    seqByType: 3,
    title: "고민과 목표",
    progressDelta: 3 / 7,
    content: <Goal_PS_3_MultiSelect_2 />,
  },
  {
    type: "goal",
    seqByType: 4,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Goal_PS_4 />,
  },
  {
    type: "goal",
    seqByType: 5,
    title: "고민과 목표",
    progressDelta: 4 / 7,
    content: <Goal_PS_5 />,
  },
  {
    type: "goal",
    seqByType: 6,
    title: "고민과 목표",
    progressDelta: 5 / 7,
    content: <Goal_PS_6 />,
  },
  {
    type: "goal",
    seqByType: 7,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Goal_PS_7 />,
  },
  {
    type: "goal",
    seqByType: 8,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Goal_PS_8 />,
  },
  {
    type: "goal",
    seqByType: 9,
    title: "고민과 목표",
    progressDelta: 6 / 7,
    content: <Goal_PS_9 />,
  },
  {
    type: "goal",
    seqByType: 10,
    hideProgress: true,
    content: <Goal_PS_10 />,
  },
  {
    type: "goal",
    seqByType: 11,
    hideBack: true,
    hideProgress: true,
    content: <Goal_PS_11 />,
  },
  {
    type: "goal",
    seqByType: 12,
    hideProgress: true,
    hideClose: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Goal_PS_12 />,
  },
];

function findIndexByStepInfo(
  steps: ProgramSetupStep[],
  type: string,
  seqByType: number
): number {
  return steps.findIndex(
    (step) => step.type === type && step.seqByType === seqByType
  );
}

export default function ProgramSetupScreen_1() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const close = useClose();

  const isInappWebview = useIsInappWebview();

  const steps = useMemo(() => {
    return [...commonSteps];
  }, []);

  // const handleBack = () => {
  //   // Determine the previous navigable step, skipping signature steps when signature is absent
  //   let targetIndex = currentStepIndex - 1;
  //
  //   const shouldSkipSignature = !isLoadingCommit && !hasSignature;
  //
  //   while (targetIndex >= 0) {
  //     const step = steps[targetIndex];
  //     const isSignatureStep = step.seq === 0 || step.seq === 14;
  //     if (isSignatureStep && shouldSkipSignature) {
  //       targetIndex -= 1;
  //       continue;
  //     }
  //     break;
  //   }
  //
  //   if (targetIndex < 0) {
  //     if (isInappWebview) {
  //       sendMessage(
  //         JSON.stringify({
  //           event: "navigate_back",
  //         })
  //       );
  //     } else {
  //       navigate("/", { replace: true });
  //       // setPCSideTab("mypage")
  //       // navigate(`/support-faq?fid=101`, { replace: true });
  //     }
  //     return;
  //   }
  //
  //   const prevStep = steps[targetIndex];
  //   navigate(`/refund?seq=${prevStep.seq}`);
  // };

  const numId = parseInt(id || "0");
  const questionNumber = searchParams.get("q");

  const currentStepIndex = Math.min(
    Math.max(findIndexByStepInfo(steps, type!!, numId), 0),
    steps.length - 1
  ); // 유효한 범위 내로 제한

  const currentStep = steps[currentStepIndex];

  const handleBack = () => {
    if (currentStepIndex <= 0) return;
    const prevStep = steps[currentStepIndex - 1];
    navigate(`/program-setup-1/${prevStep.type}/${prevStep.seqByType}`);
  };

  const { setProgress, setSectionCount } = useOnboardingProgress();

  useEffect(() => {
    if (currentStep.progressDelta) {
      let ratio = 1;
      if (questionNumber !== undefined && parseInt(questionNumber!!) > 0) {
        ratio = parseInt(questionNumber!!);
      }
      // 2개 섹션, 각 50%
      setSectionCount(2);
      if (currentStep.type === "demographic") {
        setProgress(0 + 50 * currentStep.progressDelta * ratio);
      } else if (currentStep.type === "goal") {
        let progress = 50 + 50 * currentStep.progressDelta * ratio;
        setProgress(Math.min(progress, 100)); // 100을 넘지 않도록
      }
    }
  }, [currentStep, questionNumber, setProgress]);

  const setMixPanel = useMixpanelWeb({
    eventName: "온보딩",
    params: {
      type: `${currentStep.type}`,
      pageId: `${currentStep.seqByType}`,
    },
  });

  const { mutate: savePage } = useMutation(
    ({ setupSeq, pageId }: { setupSeq: number; pageId: string }) =>
      programSetupApi.savePage(setupSeq, pageId),
    {
      onSuccess: () => {},
    }
  );

  useEffect(() => {
    if (type === "load") return;
    savePage({ setupSeq: 1, pageId: `${type}_${id}` });
    setMixPanel();
  }, [type, id]);

  return currentStep.centerFullScreen === true ? (
    <FullPageWithCenterContentV2
      content={currentStep.content}
      backgroundColor={currentStep.backgroundColor}
    />
  ) : (
    <FullPageWithHeaderAndProgress
      headerTitle={currentStep.title}
      hideHeader={currentStep.hideHeader}
      hideProgress={currentStep.hideProgress}
      paddingXContent={currentStep.paddingXContent}
      body={currentStep.content}
      backgroundColor={currentStep.backgroundColor}
      disableBack={currentStep.disableBack}
      hideBack={currentStep.hideBack}
      hideClose={currentStep.hideClose}
      onBack={currentStepIndex > 0 ? handleBack : undefined} // 첫 번째 단계에서는 뒤로가기 버튼 숨김
      onClose={close}
    />
  );
}
